/**
 * §1 "Baseline 2025 survey" — a filter bar, a two-level picker, and one card.
 *
 * The survey is 58 `mb_*` series in ten workbook sheets, and the whole of it arrives on the
 * same `/dashboard` payload the rest of the page is drawn from: this section fetches
 * nothing, exactly like `DashboardCards.tsx`, and every number it prints is the server's.
 *
 * **What changed on 2026-10-08, and why.** The 2026-09-16 rebuild (below) turned ten cards of
 * four compact rows into one full chart per sub-indicator, which was the right call against a
 * *hidden* series — but forty-odd charts stacked one under another, two to a row, turned out
 * to trade "a reader cannot find a series" for "a reader must scroll past forty charts to
 * compare two of them." Client, 2026-10-08: *"one card for the chart and a dropdown where the
 * user can choose a particular one to view, to eliminate the scroll way down to the end."* A
 * first cut offered every chart through one combined `<select>`; the client's follow-up asked
 * for the two steps to be two controls — *"first user should be able to choose from
 * agroforestry etc, then … it will fill the another dropdown with categories under that … when
 * you choose agroforestry then default first sub category will be the first on the list."* So:
 *
 * * **Sheet, then chart — two real `<select>`s, not one grouped by `<optgroup>`.** `Sheet`
 *   (`BaselineFilter`) is the top level and always points at exactly one of the ten workbook
 *   sheets — there is no "All sheets" to fall back to any more, because the whole point of a
 *   hierarchy is that a sheet is always chosen. Changing it refills the second `<select>` with
 *   that sheet's own charts and opens the first one.
 * * **The current chart is clamped to what the sheet (and the search) leave standing** —
 *   picking a different sheet, or typing a search that drops the chart on screen, hands the
 *   second `<select>` its new list's first option rather than leaving it pointed at a chart
 *   that is no longer offered.
 *
 * Everything the 2026-09-16 round decided about the *data* is unchanged and is not repeated
 * here — see `baseline.ts`: which chart kind a series gets (`chartPlan`), what is on the
 * category axis (`baselineAxis`), how the filter composes with the page's own scope.
 *
 * Two things deliberately survived every rebuild:
 *
 * * **A fallback is said out loud, once.** No region, commune or partnership was surveyed,
 *   so the server answers those scopes with the unit's *country*. A page that silently
 *   showed Burkina Faso's figures while its own heading said "Yaadga" would be a lie in the
 *   quietest possible form — the note under the section title names both geographies, and it
 *   is a fact about the whole answer, so it is printed there rather than on the card.
 * * **"View on map"** hands the open sheet its own theme key up to the page (`bl_<group_id>`),
 *   the way the catalogue's preview does: a theme is not a permalink key, so the store is
 *   written and the route changed — see `features/catalogue/preview.ts` for the full
 *   reasoning. One button now, beside the picker, rather than one per sheet.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { DashboardBaseline } from '../../lib/api';
import { Icon } from '../../lib/icons';
import { BaselineChartCard } from './BaselineCharts';
import { BaselineFilter } from './BaselineFilter';
import {
  baselineAxis, cardTitle, chartPlan, filterSheets, withHeatmap, type BaselineView,
} from './baseline';
import { sectionSpec } from './dashboard';

/**
 * "III" — read off the one ordered section spec rather than passed in.
 *
 * The subsection headers are numbered *within* the section (III.1, III.2), which needs the
 * section's own numeral; taking it as a prop would have meant a fourth prop on a component
 * two other files render, for a string that is already written down once and cannot differ.
 */
const SECTION_NUMERAL = sectionSpec.find((s) => s.key === 'baseline')?.numeral ?? 'III';

export interface BaselineSectionProps {
  baseline: DashboardBaseline;
  /**
   * The unit the *page* is scoped to, when it is one the survey could not answer for — the
   * name the fallback note owes the reader. `null` at any scope the survey covers.
   */
  unitLabel?: string | null;
  onMap: (groupId: string) => void;
  /**
   * §3 — the reader picked a survey round; the page refetches `/dashboard` with it.
   *
   * Optional, and the select is inert without it: the record page renders this same block
   * from a payload it does not control, and offering a control that changes nothing would
   * be worse than not offering one.
   */
  onPeriod?: (period: number) => void;
  /**
   * §1 — `false` when the block sits under a numbered section header that already prints
   * "Baseline 2025" (the dashboard page). The fallback note is *not* the title and stays
   * either way: it says what the figures below are an estimate for, which the section
   * header deliberately does not.
   */
  showTitle?: boolean;
}

export function BaselineSection({
  baseline,
  unitLabel,
  onMap,
  onPeriod,
  showTitle = true,
}: BaselineSectionProps) {
  const { t } = useTranslation();
  // The top level: always one real sheet, never "all" — lazy-initialised to the first so the
  // very first render already has a concrete category rather than flashing through one.
  const [sheetId, setSheetId] = useState(() => baseline.sheets[0]?.group_id ?? '');
  const [view, setView] = useState<BaselineView>('countries');
  const [query, setQuery] = useState('');
  /** The second level: a spec id, clamped below to whatever the open sheet (and search) leave. */
  const [chartId, setChartId] = useState<string | null>(null);

  // A sheet the current baseline no longer has (a refetch under a different scope, in theory —
  // the roster is the same ten sheets in practice) falls back to the first one it does have,
  // the same clamp the chart id gets below.
  useEffect(() => {
    if (baseline.sheets.length === 0) return;
    if (!baseline.sheets.some((s) => s.group_id === sheetId)) {
      setSheetId(baseline.sheets[0]!.group_id);
    }
  }, [baseline.sheets, sheetId]);

  // `filterSheets` narrows to the one open sheet and then, if there is a search, to whether
  // *that* sheet still matches it — so this is always zero or one sheet, never a roster.
  const sheets = useMemo(
    () => filterSheets(baseline.sheets, sheetId, query),
    [baseline.sheets, sheetId, query],
  );
  const sheet = sheets[0] ?? null;
  /*
    The roster the select offers, with the answered round guaranteed to be on it. A cached
    payload from before §3 carries no `periods` at all, and a server that answered a round
    it did not list would leave the select on a blank option — either way the reader would
    be looking at figures the control claims are from nowhere.
  */
  const periods = useMemo(() => {
    const listed = baseline.periods ?? [];
    const all = listed.includes(baseline.period) ? listed : [...listed, baseline.period];
    return [...new Set(all)].sort((a, b) => a - b);
  }, [baseline.periods, baseline.period]);
  /*
    One axis for the whole section, not one per chart: the picker's whole point is that only
    one chart is ever on screen, but recomputing the same four rows on every selection would
    still be work that buys nothing. It changes only when the view or the page's scope does.
  */
  const rows = useMemo(() => baselineAxis(baseline, view), [baseline, view]);
  /** The sheet's number within the section, from the *unfiltered* list — III.4 stays III.4. */
  const numeralOf = useMemo(() => {
    const index = new Map(baseline.sheets.map((s, i) => [s.group_id, `${SECTION_NUMERAL}.${i + 1}`]));
    return (groupId: string) => index.get(groupId) ?? SECTION_NUMERAL;
  }, [baseline.sheets]);
  /*
    The open sheet's own charts — the second `<select>`'s whole option list, with one more
    appended when the sheet qualifies: a summary heatmap of every practice it measures, side
    by side across the four countries (client, 2026-10-08). Watersheds have no fixed column
    count to lay a heatmap across, so the extra option is countries-view only.
  */
  const specs = useMemo(() => {
    if (!sheet) return [];
    const base = chartPlan(sheet.series, `${sheet.group_id}-`);
    if (view !== 'countries') return base;
    return withHeatmap(base, sheet.label, `${sheet.group_id}-`, t('dashboard.baseline.heatmap.option'));
  }, [sheet, view, t]);
  /**
   * The second level is clamped to the first: a sheet change, or a search, that drops the
   * chart on screen must not leave its `<select>` pointed at an option the open sheet no
   * longer offers — client, 2026-10-08: *"when you choose agroforestry then default first sub
   * category will be the first on the list."* The same rule a filtered table follows when the
   * selected row scrolls out of the result set.
   */
  useEffect(() => {
    if (specs.length === 0) return;
    if (!specs.some((s) => s.id === chartId)) setChartId(specs[0]!.id);
  }, [specs, chartId]);
  const spec = specs.find((s) => s.id === chartId) ?? specs[0] ?? null;

  if (baseline.sheets.length === 0) return null;

  // The note names the country the figures *are* from and the unit they are *not* from.
  const fallback =
    baseline.fallback && unitLabel
      ? t('dashboard.baseline.fallback', { country: baseline.scope_label, unit: unitLabel })
      : null;

  return (
    <section className="dash-baseline" data-testid="dash-baseline">
      <div className="dash-baseline-head">
        {showTitle ? (
          <h3 className="dash-baseline-title">{t('dashboard.baseline.title')}</h3>
        ) : null}
        {/* Once, under the section's own title — not once per chart. The note is about the
            whole answer, and a card is not where the scope is decided (review, fix round 1). */}
        {fallback ? (
          <p className="dash-baseline-fallback" data-testid="baseline-fallback">
            {fallback}
          </p>
        ) : null}
      </div>

      <BaselineFilter
        sheets={baseline.sheets}
        sheetId={sheetId}
        onSheet={setSheetId}
        periods={periods}
        period={baseline.period}
        onPeriod={onPeriod}
        view={view}
        onView={setView}
        query={query}
        onQuery={setQuery}
      />

      {!sheet || !spec ? (
        <p className="bl-noresults" data-testid="baseline-no-results">
          {t('dashboard.baseline.filter.empty', { q: query })}
        </p>
      ) : (
        <div className="bl-sheet" data-testid={`baseline-sheet-${sheet.group_id}`}>
          <div className="bl-sheet-head">
            <div className="bl-sheet-text">
              <h4 className="bl-sheet-title">
                <span className="bl-sheet-num" aria-hidden="true">
                  {numeralOf(sheet.group_id)}
                </span>
                {sheet.label}
              </h4>
            </div>
            <button
              type="button"
              className="btn ghost sm"
              data-testid="baseline-map"
              onClick={() => onMap(sheet.group_id)}
            >
              <Icon name="map" size={13} />
              {t('dashboard.baseline.map')}
            </button>
          </div>

          {/* The second level: flat, because it is already narrowed to the one sheet chosen
              above — no `<optgroup>` needed when there is only ever one group. */}
          <label className="bl-filter-field bl-chart-picker">
            <span>{t('dashboard.baseline.filter.chart')}</span>
            <select
              className="std"
              data-testid="baseline-chart-select"
              value={spec.id}
              onChange={(e) => setChartId(e.target.value)}
            >
              {specs.map((s) => (
                <option key={s.id} value={s.id}>
                  {cardTitle(s.title, sheet.label)}
                </option>
              ))}
            </select>
          </label>

          {/* Keyed on the spec id: a fresh element each time the picker changes, so the card's
              own entrance transition (base.css, client 2026-10-08: "a very smooth
              transitioning") plays again on every switch rather than only on first mount. */}
          <div className="bl-sheet-grid bl-sheet-grid-single">
            <BaselineChartCard
              key={spec.id}
              spec={spec}
              baseline={baseline}
              rows={rows}
              view={view}
              sheetLabel={sheet.label}
            />
          </div>
        </div>
      )}
    </section>
  );
}

export default BaselineSection;
